import React, { useState } from "react";
import { Box, Tabs, Tab, TextField, Button, IconButton, Tooltip } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useMaterialUIController } from "context";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";

const HistoryFilter = ({ onFilterChange }) => {
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode } = controller;
  const [selectedTab, setSelectedTab] = useState(0);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: dayjs().format("YYYY-MM-DDTHH:mm"),
    endDate: dayjs().format("YYYY-MM-DDTHH:mm"),
  });

  const handleTabChange = (event, newValue) => {
    console.log("newValue", newValue);
    setSelectedTab(newValue);

    const today = dayjs();
    let filterDates = {
      startDate: today.startOf("day").format("YYYY-MM-DDTHH:mm"),
      endDate: today.endOf("day").format("YYYY-MM-DDTHH:mm"),
    };

    switch (newValue + 1) {
      case 1: // Todalast 2 hours
        filterDates = {
          startDate: today.subtract(2, "hours").format("YYYY-MM-DDTHH:mm"),
          endDate: today.endOf("day").format("YYYY-MM-DDTHH:mm"),
        };
        break;
      case 2: // Today
        filterDates = {
          startDate: today.startOf("day").format("YYYY-MM-DDTHH:mm"),
          endDate: today.endOf("day").format("YYYY-MM-DDTHH:mm"),
        };

        break;
      case 3: // yesterday
        filterDates = {
          startDate: today
            .subtract(1, "day")
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm"),
          endDate: today
            .subtract(1, "day")
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm"),
        };

        break;
      case 4: // 7 days
        filterDates = {
          startDate: today
            .subtract(7, "days")
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm"),
          endDate: today
            .subtract(7, "days")
            .endOf("day")
            .format("YYYY-MM-DDTHH:mm"),
        };
        break;

      default: // Today
        break;
    }
    if (
      selectedTab === 1 ||
      selectedTab === 2 ||
      selectedTab === 3 ||
      selectedTab === 4
    ) {
      onFilterChange(filterDates);
    }
  };

  const handleCustomDateChange = (type, value) => {
    setCustomDateRange((prevRange) => ({
      ...prevRange,
      [type]: value,
    }));
  };

  const handleSearch = () => {
    onFilterChange(customDateRange);
  };

  const tabStyle = {
    marginInline: "0.1em",
    padding: "0.5em",
    minWidth: "100px", // Adjust this value to match your design
  };

  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: 3,
        display: "flex",
        alignItems: "center",
      }}
    >
      {selectedTab === 4 ? (
        <IconButton onClick={() => setSelectedTab(0)}>
          <ArrowBackIcon
            sx={{
              color: darkMode ? "#ffffff" : "#000000",
              fontSize: "1.5em",
              marginInline: "0.5em",
            }}
          />
        </IconButton>
      ) : (
        <FilterAltIcon
          sx={{
            color: darkMode ? "#ffffff" : "#000000",
            fontSize: "1.5em",
            marginInline: "0.5em",
          }}
        />
      )}

      {selectedTab === 4 ? (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={dayjs(customDateRange.startDate).format("YYYY-MM-DDTHH:mm")}
              onChange={(newValue) =>
                handleCustomDateChange("startDate", dayjs(newValue).format("YYYY-MM-DDTHH:mm"))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{
                    minWidth: "6.7em",
                    maxWidth: "6.7em",
                    svg: {
                      color: darkMode ? "#ffffff" : "#000000",
                    },
                  }}
                />
              )}
            />
            <DatePicker
              label="To Date"
              value={dayjs(customDateRange.endDate).format("YYYY-MM-DDTHH:mm")}
              onChange={(newValue) =>
                handleCustomDateChange(
                  "endDate",
                  dayjs(newValue).format("YYYY-MM-DDTHH:mm")
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{
                    minWidth: "6.7em",
                    maxWidth: "6.7em",
                    svg: {
                      color: darkMode ? "#ffffff" : "#000000",
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{
              padding: "0px",
              margin: "0px",
              maxWidth: "35px",
              minWidth: "35px",
            }}
          >
            <SearchIcon
              sx={{
                fontSize: "2em",
                padding: "0px",
                color: darkMode ? "#ffffff" : "#000000",
              }}
            />
          </Button>
        </Box>
      ) : (
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="history filter tabs"
          sx={{
            ".MuiTab-root": {
              color: "text.primary",
              ":hover": {
                color: "primary.main",
              },
            },
            ".Mui-selected": {
              backgroundColor: darkMode
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0,0,0,0.2)",
              color: "primary.main",
            },
          }}
        >
          <Tooltip title="View previous 2 hours data">
            <Tab label="Current" sx={tabStyle} />
          </Tooltip>
          <Tooltip title="View today's data">
            <Tab label="Today" sx={tabStyle} />
          </Tooltip>
          <Tooltip title="View yesterday's data">
            <Tab label="Yesterday" sx={tabStyle} />
          </Tooltip>
          <Tooltip title="View data from the last 7 days">
            <Tab label="Last 7 days" sx={tabStyle} />
          </Tooltip>
          <Tooltip title="Select a custom date range">
            <Tab label="Custom" sx={tabStyle} />
          </Tooltip>
        </Tabs>
      )}
    </Box>
  );
};

export default HistoryFilter;
